import { type KeyboardEvent, type ReactElement, type ReactNode, useRef } from 'react';
import type { EventData } from 'services/Gtm/entities';
import { trackEvent } from 'services/Gtm/functions';
import cx from 'classnames';
import * as S from './RadioStyles';

export interface RadioProps {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  children?: ReactElement | ReactElement[] | string | ReactNode;
  className?: string;
  trackingData?: EventData;
  dataCy?: string;
}

const Radio = ({
  name,
  value,
  checked,
  onChange,
  required = false,
  disabled = false,
  children,
  className = '',
  trackingData,
  dataCy,
}: RadioProps) => {
  const RadioRef = useRef<HTMLInputElement>(null);

  const handleOnChange = () => {
    if (trackEvent && trackingData) trackEvent(trackingData);
    if (onChange) {
      onChange(value);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && RadioRef.current) {
      RadioRef.current.click();
    }
  };

  return (
    <S.RadioWrapper
      data-cy={dataCy}
      className={cx(className, { disabled })}
      onKeyPress={handleKeyPress}
      tabIndex={0}
    >
      {children && (
        <S.Label
          htmlFor={value}
          className='label'
        >
          <S.RadioInput
            ref={RadioRef}
            type='Radio'
            id={value}
            name={name}
            value={value}
            checked={checked}
            onChange={handleOnChange}
            disabled={disabled}
          />
          <S.Checkmark className={`check-mark ${disabled ? 'disabled' : ''}`} />
          {children} {required && <span className='required'>*</span>}
        </S.Label>
      )}
    </S.RadioWrapper>
  );
};

export default Radio;
